import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const TimezoneContext = createContext(undefined);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * TimezoneProvider fetches the company's timezone preference from the backend
 * and provides the IANA timezone string to all children.
 *
 * When timezone is null (not set or "Flexible"), components should fall back
 * to the browser's local timezone (default behaviour of toLocaleTimeString).
 */
export const TimezoneProvider = ({ children }) => {
  const [ianaTimezone, setIanaTimezone] = useState(null); // e.g. "Asia/Singapore"
  const [tzLabel, setTzLabel] = useState(null);           // e.g. "SGT"
  const [loaded, setLoaded] = useState(false);

  const fetchTimezone = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setLoaded(true);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/company/timezone`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[TimezoneContext] API response:', data);
        if (data.success && data.timezone) {
          setIanaTimezone(data.timezone);
          setTzLabel(data.label || null);
          console.log('[TimezoneContext] Set timezone to:', data.timezone, data.label);
        } else {
          console.log('[TimezoneContext] No timezone set (null or Flexible), using browser default');
        }
      } else {
        console.warn('[TimezoneContext] API returned status:', response.status);
      }
    } catch (err) {
      console.warn('[TimezoneContext] Failed to fetch company timezone:', err);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchTimezone();
  }, [fetchTimezone]);

  /**
   * Format a Date (or ISO string) to a localized time string using the
   * company timezone.  Falls back to browser timezone when not set.
   *
   * @param {Date|string} value  - Date object or ISO string
   * @returns {string}           - e.g. "2:30 PM"
   */
  const formatTime = useCallback(
    (value) => {
      if (!value) return '';
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const options = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      };
      if (ianaTimezone) {
        options.timeZone = ianaTimezone;
      }
      const result = date.toLocaleTimeString([], options);
      return result;
    },
    [ianaTimezone],
  );

  /**
   * Format a Date (or ISO string) to a date-only string (YYYY-MM-DD)
   * in the company timezone.  Used for date separator comparisons.
   *
   * @param {Date|string} value
   * @returns {string|null} - e.g. "2026-03-12"
   */
  const getDateString = useCallback(
    (value) => {
      if (!value) return null;
      try {
        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) return null;

        if (ianaTimezone) {
          // Use Intl to get date parts in company timezone
          const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: ianaTimezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }).format(date); // returns "YYYY-MM-DD" in en-CA locale
          return parts;
        }
        // Browser timezone fallback
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d.toISOString().split('T')[0];
      } catch {
        return null;
      }
    },
    [ianaTimezone],
  );

  /**
   * Format a date for the date separator in chat (e.g. "Today (12/03/26) - Wednesday").
   * Uses company timezone to determine "today" / "yesterday".
   *
   * @param {Date|string} value
   * @returns {string|null}
   */
  const formatDateSeparator = useCallback(
    (value) => {
      if (!value) return null;
      try {
        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) return null;

        // Get date parts in company timezone
        const opts = ianaTimezone ? { timeZone: ianaTimezone } : {};
        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long', ...opts });
        const day = date.toLocaleDateString('en-US', { day: '2-digit', ...opts });
        const month = date.toLocaleDateString('en-US', { month: '2-digit', ...opts });
        const year = date.toLocaleDateString('en-US', { year: '2-digit', ...opts });

        const dateStr = `${day}/${month}/${year}`;

        // Determine "today" in company timezone
        const now = new Date();
        const todayStr = getDateString(now);
        const msgDateStr = getDateString(date);

        // Yesterday
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = getDateString(yesterday);

        if (msgDateStr === todayStr) {
          return `Today (${dateStr}) - ${dayOfWeek}`;
        } else if (msgDateStr === yesterdayStr) {
          return `Yesterday`;
        } else {
          return `${dateStr} - ${dayOfWeek}`;
        }
      } catch {
        return null;
      }
    },
    [ianaTimezone, getDateString],
  );

  return (
    <TimezoneContext.Provider
      value={{
        ianaTimezone,
        tzLabel,
        loaded,
        formatTime,
        getDateString,
        formatDateSeparator,
        refreshTimezone: fetchTimezone,
      }}
    >
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (context === undefined) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

export default TimezoneContext;
